import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadCloud, FileText, Plus, X, Edit3, Check, Trash2 } from "lucide-react";

const DEFAULT_RESUME_LABELS = ["Primary", "Engineering", "Product / PM", "Leadership", "Design / Creative"];

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({});
  const [skill, setSkill] = useState("");
  const [labels, setLabels] = useState({});
  const [labelKey, setLabelKey] = useState("");
  const [labelVal, setLabelVal] = useState("");
  const [editingLabelKey, setEditingLabelKey] = useState(null);
  const [editingLabelVal, setEditingLabelVal] = useState("");
  const [loading, setLoading] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [slotLabels, setSlotLabels] = useState({});
  const fileRefs = useRef({});

  const loadResumes = async () => {
    try {
      const r = await api.get("/profile/resumes");
      setResumes(r.data.items || []);
    } catch (err) { console.warn("[Profile] failed to load resumes:", err?.message || err); }
  };

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        headline: user.headline || "",
        bio: user.bio || "",
        skills: user.skills || [],
        location: user.location || "",
      });
      setLabels(user.profile_labels || {});
      loadResumes();
    }
  }, [user]);

  const save = async () => {
    setLoading(true);
    try {
      await api.put("/profile/me", { ...form, profile_labels: labels });
      await refresh();
      toast.success("Profile saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (!skill.trim()) return;
    setForm((f) => ({ ...f, skills: [...(f.skills || []), skill.trim()] }));
    setSkill("");
  };
  const removeSkill = (s) => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

  const addLabel = () => {
    if (!labelKey.trim() || !labelVal.trim()) return;
    setLabels((l) => ({ ...l, [labelKey.trim()]: labelVal.trim() }));
    setLabelKey(""); setLabelVal("");
  };
  const removeLabel = (k) => setLabels((l) => { const n = { ...l }; delete n[k]; return n; });
  const startEditLabel = (k, v) => { setEditingLabelKey(k); setEditingLabelVal(v); };
  const commitEditLabel = () => {
    if (editingLabelKey && editingLabelVal.trim()) {
      setLabels((l) => ({ ...l, [editingLabelKey]: editingLabelVal.trim() }));
    }
    setEditingLabelKey(null); setEditingLabelVal("");
  };

  const onUploadSlot = async (slot, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Stamp the scan-start moment BEFORE the network round trip so the
    // Dashboard's min-hold window is measured from user intent, not from
    // server ACK (which adds ~200-500ms of network latency).
    try { localStorage.setItem("karm_scan_started_at", String(Date.now())); } catch { /* noop */ }
    const fd = new FormData();
    fd.append("file", file);
    if (slotLabels[slot]) fd.append("label", slotLabels[slot]);
    try {
      await api.post(`/profile/resumes/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Resume uploaded to slot ${slot + 1}`);
      await loadResumes();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const deleteSlot = async (slot) => {
    try {
      await api.delete(`/profile/resumes/${slot}`);
      toast.success(`Slot ${slot + 1} cleared`);
      await loadResumes();
      await refresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const slotData = (slot) => resumes.find((r) => r.slot === slot);

  return (
    <div className="container py-10 max-w-3xl">
      <p className="eyebrow mb-1">My Profile</p>
      <h1 className="display-2 mb-8">Customize how the world finds you.</h1>

      <div className="vital-card-static p-6 md:p-8 mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
          </div>
          <div>
            <Label>Location</Label>
            <Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Bengaluru, India" data-testid="profile-location" />
          </div>
        </div>
        <div className="mt-4">
          <Label>Headline</Label>
          <Input value={form.headline || ""} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Senior SWE @ JPMC GCC · ex-Goldman" data-testid="profile-headline" />
        </div>
        <div className="mt-4">
          <Label>Bio</Label>
          <Textarea rows={4} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="A few lines on what makes you, you." data-testid="profile-bio" />
        </div>

        <div className="mt-6">
          <Label>Skills</Label>
          <div className="flex gap-2 mt-2">
            <Input value={skill} onChange={(e) => setSkill(e.target.value)} placeholder="Add a skill" data-testid="profile-skill-input" />
            <Button type="button" onClick={addSkill} data-testid="profile-skill-add"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {(form.skills || []).map((s) => (
              <span key={s} className="pill flex items-center gap-1" data-testid={`skill-${s}`}>
                {s}<button onClick={() => removeSkill(s)}><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Custom labels */}
      <div className="vital-card-static p-6 md:p-8 mb-6">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <p className="eyebrow">Custom Labels</p>
            <h3 className="text-lg font-bold">Brand your profile your way.</h3>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Custom labels (e.g., "Vision", "Currently building", "Open to") shown on your profile.</p>
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-2 mb-3">
          <Input value={labelKey} onChange={(e) => setLabelKey(e.target.value)} placeholder="Label (e.g. Vision)" data-testid="label-key" />
          <Input value={labelVal} onChange={(e) => setLabelVal(e.target.value)} placeholder="Value" data-testid="label-val" />
          <Button onClick={addLabel} type="button" data-testid="label-add"><Plus className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-2">
          {Object.entries(labels).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted" data-testid={`label-row-${k}`}>
              <span className="eyebrow w-32 shrink-0">{k}</span>
              {editingLabelKey === k ? (
                <Input value={editingLabelVal} onChange={(e) => setEditingLabelVal(e.target.value)} className="flex-1" />
              ) : (
                <span className="flex-1 text-sm">{v}</span>
              )}
              {editingLabelKey === k ? (
                <Button size="icon" variant="ghost" onClick={commitEditLabel}><Check className="h-4 w-4" /></Button>
              ) : (
                <Button size="icon" variant="ghost" onClick={() => startEditLabel(k, v)}><Edit3 className="h-4 w-4" /></Button>
              )}
              <Button size="icon" variant="ghost" onClick={() => removeLabel(k)} data-testid={`label-remove-${k}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* 5 Resume slots */}
      <div className="vital-card-static p-6 md:p-8 mb-6">
        <p className="eyebrow mb-1">Resumes · up to 5 variants</p>
        <h3 className="text-lg font-bold mb-1">One profile, many stories.</h3>
        <p className="text-sm text-muted-foreground mb-5">Keep a Primary + up to 4 tailored variants (e.g. Engineering, Product, Leadership). Pick the best fit per application.</p>

        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, slot) => {
            const data = slotData(slot);
            const defaultLabel = DEFAULT_RESUME_LABELS[slot];
            return (
              <div key={slot} className="rounded-lg border border-border p-4" data-testid={`resume-slot-${slot}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center text-sm font-black text-primary shrink-0">
                    {slot + 1}
                  </div>
                  <div className="flex-1 min-w-48">
                    <Input
                      value={slotLabels[slot] ?? (data?.label || defaultLabel)}
                      onChange={(e) => setSlotLabels({ ...slotLabels, [slot]: e.target.value })}
                      placeholder={defaultLabel}
                      className="h-9"
                      data-testid={`resume-label-${slot}`}
                    />
                  </div>
                  <input
                    ref={(el) => (fileRefs.current[slot] = el)}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => onUploadSlot(slot, e)}
                    className="hidden"
                    data-testid={`resume-input-${slot}`}
                  />
                  <Button
                    type="button"
                    variant={data ? "outline" : "default"}
                    size="sm"
                    onClick={() => fileRefs.current[slot]?.click()}
                    data-testid={`resume-upload-${slot}`}
                  >
                    <UploadCloud className="h-4 w-4 mr-1" />{data ? "Replace" : "Upload"}
                  </Button>
                  {data && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => deleteSlot(slot)} data-testid={`resume-delete-${slot}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
                {data ? (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <FileText className="h-3 w-3 text-primary" />
                    <span className="font-semibold text-foreground truncate">{data.filename}</span>
                    <span>· {(data.size / 1024).toFixed(1)} KB · uploaded {new Date(data.uploaded_at).toLocaleDateString()}</span>
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-muted-foreground italic">Empty — upload a PDF/DOC up to 5 MB.</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Button onClick={save} disabled={loading} size="lg" data-testid="profile-save-btn">{loading ? "Saving…" : "Save profile"}</Button>
    </div>
  );
}
